import React, { useState, useEffect, FormEvent, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageProps } from "../types/auth";
import { trackPageVisit } from "../utils/pageTracker";
import addUserIcon from "../assets/icons/user-add.svg";
import removeUserIcon from "../assets/icons/user-delete.svg";

import styles from './styles/EditGroupPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member {
  user_id: number;
  username: string;
  display_name: string;
  active_level?: {
    name: string;
  };
}

interface Curriculum {
  curriculum_id: number;
  name: string;
}

interface Group {
  group_id: number;
  name: string;
  members: Member[];
  curriculum_id?: number;
}

interface RouteParams {
  [key: string]: string | undefined;
  group_id?: string;
}

const EditGroupPage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { group_id } = useParams<RouteParams>();
  const [name, setName] = useState<string>("");
  const [curriculums, setCurriculums] = useState<Curriculum[]>([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState<number | null>(null);
  const [orgMembers, setOrgMembers] = useState<Member[]>([]);
  const [groupMembers, setGroupMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const hasTrackedRef = useRef(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Focus the name input when in New Group mode
  useEffect(() => {
    if (!group_id && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [group_id]);

  useEffect(() => {
    const fetchCurriculums = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/curriculums`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch curriculums.");
          return;
        }

        const data = await response.json();
        setCurriculums(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchCurriculums();
  }, [token, selectedOrgID]);

  const fetchGroupDetails = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${group_id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch group.");
        return;
      }

      const data: Group = await response.json();
      if (data) {
        setName(data.name || "");
        setGroupMembers(data.members || []);
        setSelectedCurriculum(data.curriculum_id || null);
      } else {
        setError("No data received for the group.");
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const fetchOrgMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/organizations/${selectedOrgID}/users`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch org members.");
        return;
      }
      const data: Member[] = await response.json();
      setOrgMembers(data || []);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  useEffect(() => {
    if (group_id) {
      fetchGroupDetails();
      fetchOrgMembers();
    }
  }, [group_id, token, selectedOrgID]);

  const handleMemberToggle = async (user_id: number) => {
    const isMember = groupMembers.some((member) => member.user_id === user_id);

    try {
      const response = await fetch(
        isMember
          ? `${API_BASE_URL}/api/groups/${group_id}/users/${user_id}`
          : `${API_BASE_URL}/api/groups/${group_id}/users`,
        {
          method: isMember ? "DELETE" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
            "Content-Type": "application/json",
          },
          body: isMember ? undefined : JSON.stringify({ user_id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update group membership.");
        return;
      }

      if (!isMember) {
        // If we're adding a member, fetch fresh group details to get level information
        await fetchGroupDetails();
      } else {
        // If removing, we can just update the state directly
        setGroupMembers((prev) => prev.filter((member) => member.user_id !== user_id));
      }
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Group name cannot be empty.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/groups/${group_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
        body: JSON.stringify({
          name,
          org_id: selectedOrgID ? parseInt(selectedOrgID.toString(), 10) : 0,
          curriculum_id: selectedCurriculum || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to save group.");
        return;
      }
      const data: Group = await response.json();
      navigate(`/groups/${data.group_id}`);
    } catch (err) {
      setError("An unexpected error occurred.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Edit Group</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <form onSubmit={handleUpdate} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Group Name:</label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className={styles.input}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Curriculum:</label>
          <select
            value={selectedCurriculum?.toString() || ""}
            onChange={(e) => setSelectedCurriculum(e.target.value ? parseInt(e.target.value, 10) : null)}
            className={styles.select}
          >
            <option value="">No curriculum</option>
            {curriculums.map((curriculum) => (
              <option key={curriculum.curriculum_id} value={curriculum.curriculum_id}>
                {curriculum.name}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className={styles.submitButton}>
          {group_id ? "Update" : "Save"}
        </button>
      </form>

      <div className={styles.membersSection}>
        <h3 className={styles.membersTitle}>Members</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Member</th>
              <th className={styles.tableHeader}>Level</th>
              <th className={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupMembers.length > 0 ? (
              groupMembers.map((member) => (
                <tr key={member.user_id} className={styles.tableRow}>
                  <td className={styles.tableCell}>
                    <a href={`/members/${member.user_id}`} className={styles.memberLink}>
                      {member.display_name 
                        ? `${member.display_name} (${member.username})`
                        : member.username}
                    </a>
                  </td>
                  <td className={styles.tableCell}>
                    {member.active_level?.name || 
                      (curriculums.find(c => c.curriculum_id === selectedCurriculum)?.name 
                        ? <span className={styles.notEnrolled}>
                            Not enrolled in {curriculums.find(c => c.curriculum_id === selectedCurriculum)?.name}
                          </span>
                        : "-"
                      )}
                  </td>
                  <td className={styles.tableCell}>
                    <button
                      onClick={() => handleMemberToggle(member.user_id)}
                      className={styles.iconButton}
                    >
                      <img src={removeUserIcon} alt="Remove member" className={styles.actionIcon} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className={styles.tableCell}>No members in group.</td>
              </tr>
            )}
          </tbody>
        </table>

        <h3 className={`${styles.membersTitle} ${styles.nonGroupMembersTitle}`}>Non-Members</h3>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Member</th>
              <th className={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgMembers.filter(member => !groupMembers.some(gm => gm.user_id === member.user_id)).length > 0 ? (
              orgMembers
                .filter(member => !groupMembers.some(gm => gm.user_id === member.user_id))
                .map((member) => (
                  <tr key={member.user_id} className={styles.tableRow}>
                    <td className={styles.tableCell}>
                      <a href={`/members/${member.user_id}`} className={styles.memberLink}>
                        {member.display_name 
                          ? `${member.display_name} (${member.username})`
                          : member.username}
                      </a>
                    </td>
                    <td className={styles.tableCell}>
                      <button
                        onClick={() => handleMemberToggle(member.user_id)}
                        className={styles.iconButton}
                      >
                        <img src={addUserIcon} alt="Add member" className={styles.actionIcon} />
                      </button>
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={2} className={styles.tableCell}>No available members to add.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EditGroupPage;
